import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Icon from '../ui/Icon';
import FieldError from '../ui/FieldError';
import { stationsApi } from '../../api/stations';

const TYPES = [
  { key: 'all', label: 'All' },
  { key: 'fuel', label: 'Fuel' },
  { key: 'ev', label: 'EV' },
];
const MIN_QUERY = 2;

const input =
  'w-full h-10 px-3 rounded-lg text-[13px] text-on-surface bg-surface-container-low border border-outline-variant/30 outline-none transition placeholder:text-on-surface-variant/60 focus:bg-surface-container-lowest focus:border-primary-container focus:shadow-[0_0_0_3px_rgba(34,197,94,0.15)]';

/**
 * Add mode: find a station with Google Places (Netherlands), or paste a Place ID.
 * `value` is `{ placeId, name, address }` or null; `error` is a validation message.
 */
export default function StationPicker({ value, onChange, error }) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [type, setType] = useState('all');
  const [manual, setManual] = useState(false);
  const [manualId, setManualId] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 350);
    return () => clearTimeout(timer);
  }, [query]);

  const search = useQuery({
    queryKey: ['googleStations', debounced, type],
    queryFn: ({ signal }) => stationsApi.searchGoogleStations(debounced, type, { signal }),
    enabled: debounced.length >= MIN_QUERY && !value,
    staleTime: 60_000,
    retry: false,
  });

  // Google unavailable / timed out / over quota: offer the Place ID field straight away
  const googleDown = [502, 503, 504, 429].includes(search.error?.status);
  useEffect(() => {
    if (googleDown) setManual(true);
  }, [googleDown]);

  if (value) {
    return (
      <div>
        <div className="flex items-baseline justify-between mb-1">
          <span className="text-xs font-bold text-on-surface">Target Station (placeId)</span>
          <span className="text-[11px] font-semibold text-secondary">Active Selection</span>
        </div>
        <div className="flex items-start gap-2 p-3 rounded-lg bg-surface-container-low border border-outline-variant/30">
          <Icon name="location_on" size={18} className="text-primary-container mt-0.5" />
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-semibold text-on-surface truncate">{value.name ?? 'Name unavailable'}</p>
            {value.address && <p className="text-xs text-on-surface-variant truncate">{value.address}</p>}
            <p className="font-data-mono text-[10px] text-on-surface-variant break-all">{value.placeId}</p>
          </div>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-xs font-semibold text-secondary hover:underline rounded outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <label htmlFor="station-search" className="text-xs font-bold text-on-surface">
          Add / Search Station
        </label>
        <span className="inline-flex items-center gap-1 font-data-mono text-[10px] text-secondary bg-surface-container-low px-1.5 py-0.5 rounded">
          <Icon name="travel_explore" size={12} />
          Google Maps API
        </span>
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Icon name="location_on" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant pointer-events-none" />
          <input
            id="station-search"
            type="search"
            autoComplete="off"
            maxLength={200}
            placeholder="Search station by name or address…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-invalid={Boolean(error) || undefined}
            aria-describedby="station-search-hint"
            className={`${input} pl-9`}
          />
        </div>
        <div role="radiogroup" aria-label="Station type" className="flex p-0.5 rounded-lg bg-surface-container-low border border-outline-variant/30">
          {TYPES.map((t) => (
            <button
              key={t.key}
              type="button"
              role="radio"
              aria-checked={type === t.key}
              onClick={() => setType(t.key)}
              className={`px-2.5 rounded-md text-xs font-semibold outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot ${
                type === t.key ? 'bg-surface-container-lowest text-primary shadow-xs' : 'text-on-surface-variant hover:text-on-surface'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <p id="station-search-hint" className="text-[11px] text-on-surface-variant">
        Netherlands only, up to 10 results. Type at least {MIN_QUERY} characters.
      </p>

      {search.isFetching && (
        <p className="flex items-center gap-1.5 text-xs text-on-surface-variant" role="status">
          <Icon name="progress_activity" size={14} className="animate-spin text-secondary" />
          Searching Google…
        </p>
      )}

      {search.error && (
        <div role="alert" className="flex items-start gap-2 rounded-lg border border-status-alert-border bg-status-alert-bg px-3 py-2 text-xs text-status-alert-text">
          <Icon name="error" size={16} />
          <span>{search.error.message}</span>
        </div>
      )}

      {search.data && !search.isFetching && debounced.length >= MIN_QUERY && (
        search.data.length === 0 ? (
          <p className="text-xs text-on-surface-variant">No stations found for “{debounced}”.</p>
        ) : (
          <ul className="max-h-64 overflow-y-auto rounded-lg border border-outline-variant/30 divide-y divide-surface-container" aria-label="Google results">
            {search.data.map((r) => (
              <li key={r.placeId}>
                <button
                  type="button"
                  onClick={() => onChange({ placeId: r.placeId, name: r.name, address: r.address })}
                  className="w-full text-left px-3 py-2 hover:bg-surface-container-low outline-none focus-visible:bg-surface-container-low"
                >
                  <span className="block text-[13px] font-semibold text-on-surface">{r.name ?? r.placeId}</span>
                  {r.address && <span className="block text-xs text-on-surface-variant">{r.address}</span>}
                  <span className="mt-1 flex flex-wrap gap-1">
                    {r.alreadyConfigured && (
                      <span className="px-1.5 rounded-full text-[10px] font-semibold bg-status-ok-bg text-status-ok-text border border-status-ok-border">
                        Already in Pompi ({r.configuredFuelCount} {r.configuredFuelCount === 1 ? 'fuel' : 'fuels'})
                      </span>
                    )}
                    {!r.isFuelStation && (
                      <span className="px-1.5 rounded-full text-[10px] font-semibold bg-status-pending-bg text-status-pending-text border border-status-pending-border">
                        Not a fuel station
                      </span>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )
      )}

      {manual ? (
        <div className="pt-1">
          <label htmlFor="manual-place-id" className="block text-xs font-bold text-on-surface mb-1">
            Or paste a Google Place ID
          </label>
          <div className="flex gap-2">
            <input
              id="manual-place-id"
              type="text"
              autoComplete="off"
              spellCheck={false}
              placeholder="ChIJ…"
              value={manualId}
              onChange={(e) => setManualId(e.target.value)}
              aria-invalid={Boolean(error) || undefined}
              aria-describedby={error ? 'station-error' : undefined}
              className={`${input} font-data-mono text-xs`}
            />
            <button
              type="button"
              disabled={!manualId.trim()}
              onClick={() => onChange({ placeId: manualId.trim(), name: null, address: null, manual: true })}
              className="h-10 px-3 rounded-lg text-xs font-semibold bg-surface-container text-on-surface hover:bg-surface-container-high disabled:opacity-50 disabled:cursor-not-allowed outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
            >
              Use ID
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setManual(true)}
          className="text-xs font-semibold text-secondary hover:underline rounded outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
        >
          Enter a Place ID manually
        </button>
      )}
      <FieldError id="station-error">{error}</FieldError>
    </div>
  );
}
